import type { Tenant } from '@/types';
import type { VisitOptions } from '@inertiajs/core';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Converts a Date to a YYYY-MM-DD string using local time (not UTC).
 * Using toISOString() would shift the date for users in UTC+ timezones.
 */
export function toInputDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function formatDate(date: Date | string | null | undefined, locale: string = 'de-DE') {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(locale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });
}

export function formatDateTime(date: Date | string | null | undefined, locale: string = 'de-DE') {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function formatDateNumeric(date: Date | string | null | undefined, locale: string = 'de-DE') {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

export function formatSeconds(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export function formatCurrency(amount: number, currency: string = 'EUR', locale: string = 'de-DE') {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

export function formatInterestRate(rate: number | string | null | undefined, locale: string = 'de'): string {
    const value = coerceInterestRate(rate);

    if (!Number.isFinite(value)) {
        return '';
    }

    return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(value);
}

/**
 * Settings are stored as strings. Accept machine ("0.1") and German ("0,1") notation
 * so a non-float payload still renders instead of an empty "%".
 */
function coerceInterestRate(rate: number | string | null | undefined): number {
    if (typeof rate === 'number') {
        return rate;
    }

    if (typeof rate !== 'string') {
        return Number.NaN;
    }

    const trimmed = rate.trim();

    if (trimmed === '') {
        return Number.NaN;
    }

    const normalized = trimmed.includes(',') ? trimmed.replace(/\./g, '').replace(',', '.') : trimmed;

    return Number(normalized);
}

export function formatTenantsName(tenants: Pick<Tenant, 'first_name' | 'last_name'>[]) {
    return tenants.map((tenant) => `${tenant.first_name} ${tenant.last_name}`).join(', ');
}

export function optionsWithRedirect(options: Omit<VisitOptions, 'data'>, redirectUrl?: string): Omit<VisitOptions, 'data'> {
    const baseOptions: Omit<VisitOptions, 'data'> = {
        preserveScroll: true,
        ...options,
    };

    if (redirectUrl) {
        return {
            ...baseOptions,
            headers: {
                ...baseOptions.headers,
                'X-Intended-Redirect': redirectUrl,
            },
        };
    }

    return baseOptions;
}

let installmentClientKeyCounter = 0;

export function createInstallmentClientKey(): string {
    installmentClientKeyCounter += 1;

    return `installment-${installmentClientKeyCounter}`;
}

/**
 * SHA-1 is required by the Pwned Passwords range API (k-anonymity model) and is
 * not used as a security primitive here.
 */
// nosemgrep: nodejs_scan.javascript-crypto-rule-node_sha1
export async function sha1Hex(input: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const digest = await crypto.subtle.digest('SHA-1', data);
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (let i = 0; i < bytes.length; i++) {
        hex += bytes[i].toString(16).padStart(2, '0');
    }
    return hex.toUpperCase();
}

export function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatIban(iban: string): string {
    // Remove any existing spaces
    const cleanIban = iban.replace(/\s/g, '');

    // Add space every 4 characters
    return cleanIban.replace(/(.{4})/g, '$1 ').trim();
}

export function safeOpen(url: string | null | undefined): void {
    if (!url) return;

    try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
        window.open(parsed.href, '_blank', 'noopener,noreferrer');
    } catch {
        // ignore invalid URLs
    }
}

export async function copyToClipboard(text: string, successMessage?: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        if (successMessage) {
            const { toast } = await import('sonner');
            toast.success(successMessage);
        }
        return true;
    } catch {
        return false;
    }
}
